import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import { centsToDollarsNumber } from '../../lib/util';
import styles from '../../styles/lineItem.module.scss';
import * as types from '../../types';
import { Image } from '../Image';
import RemoveLineItemButton from '../RemoveLineItemButton';
import Dropdown from '../Dropdown';
import { cartActions } from '../../reducers/storeCart';
import { useDispatch } from 'react-redux';
import { ReactComponent as GiftIcon } from '../../assets/images/icons/gift.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/icons/icon_info_transparent.svg';
import { SimpleDismissableModal } from '../modals';
import * as events from '../../lib/analytics/events';
import { getFiGrowthBook } from '../../lib/growthbook';
import { useLocale } from '../../contexts/LocaleContext';
import { Flex, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import FreeTrialLearnMoreModal from '../FreeTrialLearnMoreModal';
/**
 * Defines an individual pricing row to display for a cart item. In the case of bundles, we want to list out
 * all of the products in the bundle with a rolled up price. A cart item could have multiple pricing rows
 * e.g. we want to split out subscription activation fee price to display separately.
 */
export interface ItemPriceRowProps {
  discountedFromOriginalPriceInCents?: number;
  freeTrialLengthInDays?: number;
  productName: string;
  secondary?: boolean;
  totalPriceInCents?: number;
  variantDescription?: string;
  isFreeTrialRow?: boolean;
}

interface ProductCartItemProps {
  id: types.CartItemID;
  sku: string;
  priceRows: ItemPriceRowProps[];
  productImageUrl?: string;
  removeCartItem?: () => void;
  updateGiftItem?: (isGift: boolean) => void;
  isGift?: boolean;
  quantity?: number;
  applyGreyBackground?: boolean;
  applyBackgroundBlend?: boolean;
  /** Removes the cart-test card outline/border (e.g. for the standalone Ultra hardware line item). */
  flat?: boolean;
  /** Render round prices without a trailing ".00" (cart-test whole-dollar style). */
  wholeDollarPrices?: boolean;
  isFreeTrialItem?: boolean;
  freeTrialShippingPrice?: string;
  freeTrialSubscriptionPrice?: string;
}

function TariffFeeModalContent({ close }: { close: () => void }) {
  const { t } = useTranslation();
  return (
    <div className={styles.tariffFee}>
      <div className={styles.tariffFeeTitle}>{t('why_is_there_a_tariff_fee')}</div>
      <div className={styles.tariffFeeSubtitle}>{t('tariff_fee_subtitle')}</div>
      <div className={styles.tariffFeeDescription}>{t('tariff_fee_description', { amount: 50 })}</div>
      <button
        className={styles.tariffFeeButton}
        onClick={() => {
          close();
        }}
      >
        {t('got_it')}
      </button>
    </div>
  );
}

function TariffFeeModal() {
  return (
    <SimpleDismissableModal
      closeIconClassName={styles.closeIcon}
      trigger={
        <div className={styles.infoIconWrapper}>
          <InfoIcon className={styles.infoIcon} />
        </div>
      }
      onOpen={() => {
        events.cartPage.tariffFeeModalOpen();
      }}
      onClose={() => {
        events.cartPage.tariffFeeModalClose();
      }}
    >
      {(close) => <TariffFeeModalContent close={close} />}
    </SimpleDismissableModal>
  );
}

function ItemPriceRow({
  discountedFromOriginalPriceInCents,
  freeTrialLengthInDays,
  productName,
  secondary,
  totalPriceInCents,
  variantDescription,
  isFreeTrialRow,
  wholeDollarPrices,
}: ItemPriceRowProps & { wholeDollarPrices?: boolean }) {
  const { t } = useTranslation();
  const discounted = !!discountedFromOriginalPriceInCents;
  const waived = totalPriceInCents === 0 && discounted;
  const { currencyFormatter } = useLocale();

  const formatPrice = (cents: number) => {
    const formatted = currencyFormatter.format(centsToDollarsNumber(cents));
    return wholeDollarPrices ? formatted.replace(/\.00$/, '') : formatted;
  };

  return (
    <div
      className={classNames(styles.lineItemPriceRow, {
        [styles.secondaryPriceRow]: !!secondary,
        [styles.waived]: waived,
      })}
    >
      <div className={styles.lineItemDetails}>
        <div className={styles.lineItemDetailsRow}>
          <div className={styles.productName}>
            {productName}
            {!secondary && variantDescription && (
              // eslint-disable-next-line react/jsx-no-literals
              <span className={styles.variantDescription}>&nbsp;({variantDescription})</span>
            )}
            {secondary && variantDescription && (
              <div className={styles.secondaryVariantDescription}>&nbsp;{variantDescription}</div>
            )}
            {productName === t('tariff_fee') && <TariffFeeModal />}
          </div>
        </div>
      </div>

      <div
        className={classNames(styles.lineItemPrices, {
          [styles.freeTrial]: !!freeTrialLengthInDays,
        })}
      >
        {!!discountedFromOriginalPriceInCents && (
          <div className={styles.lineItemOriginalPrice}>{formatPrice(discountedFromOriginalPriceInCents)}</div>
        )}
        {(!!totalPriceInCents || (isFreeTrialRow && totalPriceInCents === 0)) && (
          <div
            className={classNames(styles.lineItemPrice, {
              [styles.credit]: totalPriceInCents < 0,
            })}
          >
            {formatPrice(totalPriceInCents)}
          </div>
        )}
        {freeTrialLengthInDays && (
          <div className={styles.freeTrialLength}>{t('free_for_days', { days: freeTrialLengthInDays })}</div>
        )}
      </div>
    </div>
  );
}

export default function ProductCartItem({
  id,
  sku,
  priceRows,
  productImageUrl,
  removeCartItem,
  updateGiftItem,
  isGift,
  quantity,
  applyGreyBackground,
  applyBackgroundBlend,
  flat,
  wholeDollarPrices,
  isFreeTrialItem,
  freeTrialShippingPrice,
  freeTrialSubscriptionPrice,
}: ProductCartItemProps) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [currentQuantity, setCurrentQuantity] = useState(quantity);
  const quantityOptions = useMemo(() => {
    const list = [];
    for (let i = 1; i <= 20; i++) {
      list.push({ value: i, label: i.toString() });
    }
    return list;
  }, []);
  const selectedQuantity = useMemo(() => {
    if (!currentQuantity) {
      return;
    }

    return quantityOptions.find(({ value }) => value === currentQuantity);
  }, [quantityOptions, currentQuantity]);
  const showEcomCartTest = getFiGrowthBook().getFeatureValue('ecom-cart-test', false);

  return (
    <div className={classNames(styles.lineItem, { [styles.flat]: flat })}>
      {removeCartItem && !showEcomCartTest && (
        <div className={styles.removeButtonRow}>
          <RemoveLineItemButton onClick={removeCartItem} />
        </div>
      )}
      <div className={styles.itemRow}>
        <div
          className={classNames(styles.lineItemImage, {
            [styles.empty]: !productImageUrl,
            [styles.greyBackground]: applyGreyBackground,
            [styles.backgroundBlend]: applyBackgroundBlend,
          })}
        >
          {productImageUrl && (
            <Image
              className={classNames({ [styles.miniImage]: sku === 'M1-MUA' })}
              image={{ type: 'image', url: productImageUrl }}
              alt={priceRows[0].productName}
            />
          )}
        </div>
        <div className={styles.lineItemBody}>
          <div className={styles.lineItemPriceRows}>
            {priceRows.map((priceRow, i) => (
              <ItemPriceRow
                discountedFromOriginalPriceInCents={priceRow.discountedFromOriginalPriceInCents}
                freeTrialLengthInDays={priceRow.freeTrialLengthInDays}
                key={i}
                productName={priceRow.productName}
                secondary={priceRow.secondary}
                totalPriceInCents={priceRow.totalPriceInCents}
                variantDescription={priceRow.variantDescription}
                isFreeTrialRow={priceRow.isFreeTrialRow}
                wholeDollarPrices={wholeDollarPrices}
              />
            ))}
            {isFreeTrialItem && freeTrialShippingPrice && freeTrialSubscriptionPrice && (
              <div className={styles.freeTrialFinePrint}>
                <span>{t('free_trial_cart_fine_print')}</span>{' '}
                <FreeTrialLearnMoreModal
                  trigger={
                    <span className={styles.freeTrialLearnMore}>
                      {t('free_trial_learn_more')} {'›'}
                    </span>
                  }
                  onOpen={() => events.freeTrialLearnMoreClickedFromCart()}
                  subscriptionPrice={freeTrialSubscriptionPrice}
                />
              </div>
            )}
          </div>
          {isGift && (
            <Flex
              align="center"
              gap="8px"
              mb={10}
              py={4}
              px={8}
              bg="background-light-tertiary"
              w="fit-content"
              style={{ borderRadius: 8 }}
            >
              <GiftIcon />
              <Text fz={14} fw={500} lh={1.4}>
                {t('gift_purchase')}
              </Text>
            </Flex>
          )}
          {sku !== 'F1-N-20' && quantity && quantity > 1 ? (
            // eslint-disable-next-line react/jsx-no-literals
            <div className={styles.lineItemQuantity}>x {quantity}</div>
          ) : null}
          {sku === 'F1-N-20' ? (
            <div className={styles.lineItemQuantity}>
              <span className={styles.quantityTitle}>{t('quantity')}</span>
              <div className={classNames('cart-quantity-container', styles.quantityContainer)}>
                <Dropdown
                  id="cartQuantity"
                  options={quantityOptions}
                  selectedOption={selectedQuantity}
                  onChange={(option) => {
                    if (option) {
                      setCurrentQuantity(option.value);
                      dispatch(cartActions.updateCartItemQuantity({ id, quantity: option.value }));
                    }
                  }}
                />
              </div>
            </div>
          ) : null}
          {removeCartItem && showEcomCartTest && (
            <div className={styles.removeButton} onClick={removeCartItem}>
              {t('remove')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
